

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useCurrentUser } from 'app';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isAuthError: boolean;
  retryCount: number;
}

// Auth error recovery component
const AuthErrorRecovery: React.FC<{ onRetry: () => void; retryCount: number }> = ({ onRetry, retryCount }) => {
  const { user, loading } = useCurrentUser();
  
  // Automatically retry if auth has recovered
  React.useEffect(() => {
    if (!loading && user && retryCount < 3) {
      const timer = setTimeout(() => {
        onRetry();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, loading, onRetry, retryCount]);

  if (loading || retryCount < 3) {
    // Auto-retry window — show a minimal, neutral spinner so we never
    // flash the scary "Authentication Issue" panel during the normal
    // ~1s auth-settling window on page load. Only the exhausted-retries
    // branch below is allowed to surface the recovery UI.
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {retryCount > 0 ? `Reconnecting... (attempt ${retryCount + 1})` : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // If we have exhausted retries, show manual recovery
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
      <div className="text-center max-w-md bg-white p-6 rounded-lg shadow-lg">
        <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Authentication Issue</h2>
        <p className="text-gray-600 mb-6">
          We're having trouble with your authentication. This usually resolves by refreshing the page.
        </p>
        <div className="space-y-3">
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Refresh Page
          </button>
          <button 
            onClick={onRetry}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

class AuthErrorBoundary extends Component<Props, State> {
  private retryTimer: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isAuthError: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Surface the underlying error in the dev tools console — the
    // boundary used to swallow it silently, which made the "flash of
    // error then recovers" reports impossible to diagnose.
    try {
      console.warn(
        '[AuthErrorBoundary] caught render error:',
        error?.message,
        '\nstack:', error?.stack,
      );
    } catch {
      /* never let logging itself break the boundary */
    }
    // Check if this is an auth-related error
    const errorMessage = error.message || '';
    const errorStack = error.stack || '';
    
    // Check if this is a chunk loading error (module import failed)
    const isChunkLoadError = 
      errorMessage.includes('Importing a module script failed') ||
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError');
    
    // If it's a chunk load error, reload the page to get fresh chunks. We
    // also clear the service-worker cache + unregister any SW so the old
    // chunk hashes don't keep getting served from disk. Cooldown prevents
    // an infinite reload loop if the new chunks are also broken.
    //
    // DEV mode: skip the reload. Vite HMR rewrites chunks constantly,
    // so transient dynamic-import failures are normal and a full page
    // reload wipes the user's session mid-edit. Just log and let the
    // boundary render — the user (or HMR) will recover on next nav.
    if (isChunkLoadError) {
      const isDev = typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV;
      if (isDev) {
        // DEV mode: do NOT auto-reload. The previous version reloaded
        // every 30 seconds on persistent chunk errors, which created
        // the appearance of "the preview refreshes every 30 seconds".
        // The root cause is usually a 429 rate-limit cascade — a
        // reload just retries the same N+1 request storm and the
        // platform's edge rate-limiter throws 429s on Vite's own JS
        // files (@react-refresh / client / main.tsx). Reloading
        // through that loop makes everything worse. Let the boundary
        // render the error so the user can investigate.
        console.warn('[AuthErrorBoundary] Dev chunk load error — NOT auto-reloading. Hard refresh manually if needed.');
        return { hasError: true, error, isAuthError: false };
      }
      console.warn('[AuthErrorBoundary] Chunk load error detected — wiping caches and reloading');
      const lastReload = sessionStorage.getItem('lastChunkErrorReload');
      const now = Date.now();
      const reloadCount = parseInt(sessionStorage.getItem('chunkErrorReloadCount') || '0', 10);
      // Cooldown: 5s between reloads, hard-stop after 4 attempts so we
      // never lock the user out forever.
      if ((!lastReload || (now - parseInt(lastReload)) > 5000) && reloadCount < 4) {
        sessionStorage.setItem('lastChunkErrorReload', now.toString());
        sessionStorage.setItem('chunkErrorReloadCount', String(reloadCount + 1));
        // Best-effort cache wipe — async, but we kick the reload
        // immediately so the page doesn't sit broken.
        try {
          if ('caches' in window) {
            caches.keys().then((keys) => keys.forEach((k) => caches.delete(k))).catch(() => {});
          }
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((regs) => {
              regs.forEach((r) => r.unregister().catch(() => {}));
            }).catch(() => {});
          }
        } catch {
          // ignore — we still want the reload to happen
        }
        window.location.reload();
      } else if (reloadCount >= 4) {
        console.error('[AuthErrorBoundary] Chunk reload loop limit hit — staying on error page');
      }
    }
    
    const isAuthError = 
      errorMessage.includes('Cannot read properties of null') ||
      errorMessage.includes('Cannot read property') ||
      errorMessage.includes('uid') ||
      errorMessage.includes('useUserGuardContext must be used within') ||
      errorMessage.includes('auth') ||
      errorStack.includes('UserGuard') ||
      errorStack.includes('useCurrentUser') ||
      errorStack.includes('firebase/auth');

    return {
      hasError: true,
      error,
      isAuthError
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    try {
      console.warn(
        '[AuthErrorBoundary] componentDidCatch — componentStack:',
        errorInfo?.componentStack,
      );
    } catch {
      /* swallow */
    }
    this.setState({
      error,
      errorInfo
    });
  }

  componentWillUnmount() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      isAuthError: false,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      // Show auth-specific recovery for auth errors
      if (this.state.isAuthError) {
        return (
          <AuthErrorRecovery 
            onRetry={this.handleRetry} 
            retryCount={this.state.retryCount}
          />
        );
      }

      // Show generic error boundary for other errors
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
          <div className="text-center max-w-md bg-white p-6 rounded-lg shadow-lg">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-4 text-gray-900">Something went wrong</h2>
            <p className="text-gray-600 mb-6">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => window.location.reload()}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Refresh Page
              </button>
              <button 
                onClick={this.handleRetry}
                className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
